import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import {
  createFestivals,
  getAllActiveFestivals,
  getAllFestivals,
  getFestivalsByID,
  updateFestivals,
} from "./services/festivals.services";
import { clearFestivalsDetails } from "./festivalsSlice";
import { STATUSARRAY } from "../../_contstants/common";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import IconButton from "../../components/common/IconButton";

const CreateFestivals: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const FestivalsDetail = useAppSelector(
    (state) => state.festivals.FestivalsDetail
  );
  const FestivalsID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.festivals.formLoading);
  const isIndirect = useAppSelector((state) => state.drawer.isIndirect);

  useEffect(() => {
    if (FestivalsID) {
      dispatch(getFestivalsByID(FestivalsID));
    }
  }, [FestivalsID]);

  useEffect(() => {
    return () => {
      dispatch(clearFestivalsDetails());
      dispatch(closed());
    };
  }, []);

  const handleSubmit = async (values: any) => {
    try {
      if (!FestivalsID) {
        const insertPayload = {
          Title: values.Title,
          Description: values.Description,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createFestivals(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearFestivalsDetails());

          if (isIndirect) {
            dispatch(getAllActiveFestivals());
          } else {
            dispatch(getAllFestivals());
          }
        }
      } else {
        const editPayload = {
          ID: FestivalsID,
          Title: values.Title,
          Description: values.Description,

          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateFestivals(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearFestivalsDetails());
          dispatch(getAllFestivals());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={FestivalsDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem className="mui-drawer-header">
              <Typography.h4 className="m-0">
                {FestivalsID ? "Update Festivals" : "Add Festivals"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
                icon={RxCross1}
              />
            </GridLayoutItem>

            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Title"
                label="Title"
                placeholder="i.e Diwali"
                component={FormTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>

            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="Description"
                label="Description"
                component={FormRichTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>

            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="IsActive"
                label="Status"
                component={FormSelectionField}
                astrike={true}
                validator={requiredValidator}
                placeholder="i.e. Active"
                options={STATUSARRAY?.map((status: any) => {
                  return {
                    value: status?.value,
                    label: status?.label,
                  };
                })}
              />
            </GridLayoutItem>

            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "20px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={!FestivalsID ? "Save" : "Update"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />

              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 10 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreateFestivals;
